import { API_BASE_URL } from '@synergy/config';
import { Box, List, ListItemButton, ListItemText, TextField, styled } from '@mui/material';
import { ComponentProps, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolCheckbox } from '@synergy/components-next';
import { getLocalizedString, unique } from '@synergy/utils';

import { CategoryApi, CategorySearchHit } from '../../api/category-api';
import { PoolCategoryTreeItems } from './pool-category-tree-items';
import { useAppSelector } from '../../../store/use-appstate-selector';

const CategoryListContainer = styled(List)(({ theme }) => ({
    overflow: 'auto',
    border: `1.5px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
    maxHeight: 400,
    backgroundColor: theme.palette.background.paper,
}));

export interface PoolCategoryTreeProps extends Omit<ComponentProps<typeof CategoryListContainer>, 'onChange'> {
    selectedIds?: number[];
    autoSelectParents?: boolean;
    onChange?: (categoryIds: number[]) => void;
    /**
     * A search field above the tree. The tree opens one level at a time, and
     * finding a sub-category meant knowing where to look first (key-user brief
     * A3.1). A hit shows its path and ticks like a row of the tree.
     */
    searchable?: boolean;
}

const MIN_SEARCH = 2;

export const PoolCategoryTree = (props: PoolCategoryTreeProps) => {
    const {
        selectedIds: value = [],
        onChange,
        autoSelectParents = false,
        searchable = false,
        ...containerProps
    } = props;
    const intl = useIntl();
    const locales = useAppSelector(state => state.supportedLocales);
    const [search, setSearch] = useState('');
    const [hits, setHits] = useState<CategorySearchHit[] | null>(null);

    const term = search.trim();

    useEffect(() => {
        if (!searchable || term.length < MIN_SEARCH) {
            setHits(null);
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            const response = await new CategoryApi(API_BASE_URL).searchCategories(term, controller.signal);
            if (response.success) {
                setHits(response.result);
            }
        }, 300);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [searchable, term]);

    const handleSelect = (id: number, path: number[]) => {
        let nextSelectedIds = [...value, id];
        if (autoSelectParents) {
            nextSelectedIds = unique([...nextSelectedIds, ...path]);
        }
        onChange?.(nextSelectedIds);
    };
    const handleDeselect = (id: number) => {
        onChange?.(value.filter(selectedId => selectedId !== id));
    };

    const name = (translations: CategorySearchHit['translations']): string => getLocalizedString(locales, translations);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {searchable && (
                <TextField
                    size="small"
                    fullWidth={true}
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder={intl.formatMessage({
                        id: 'app.categories.searchPlaceholder',
                        defaultMessage: 'Kategorie suchen …',
                    })}
                />
            )}
            <CategoryListContainer {...containerProps}>
                {null === hits ? (
                    <PoolCategoryTreeItems
                        path={[]}
                        onSelect={handleSelect}
                        onDeselect={handleDeselect}
                        selectedIds={value}
                    />
                ) : hits.length === 0 ? (
                    <Box sx={{ p: 2, fontSize: 13, color: 'text.secondary' }}>
                        <FormattedMessage id="app.common.emptyResult" />
                    </Box>
                ) : (
                    hits.map(hit => {
                        const checked = value.includes(hit.id);
                        const toggle = () =>
                            checked
                                ? handleDeselect(hit.id)
                                : handleSelect(
                                      hit.id,
                                      hit.ancestors.map(ancestor => ancestor.id)
                                  );

                        return (
                            <ListItemButton
                                key={hit.id}
                                onClick={toggle}
                                sx={{ height: 38, py: 0, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
                            >
                                <PoolCheckbox variant="round" checked={checked} />
                                <ListItemText
                                    primary={name(hit.translations)}
                                    secondary={hit.ancestors.map(ancestor => name(ancestor.translations)).join(' › ')}
                                    primaryTypographyProps={{ fontSize: 13 }}
                                    secondaryTypographyProps={{ fontSize: 11 }}
                                    sx={{ my: 0 }}
                                />
                            </ListItemButton>
                        );
                    })
                )}
            </CategoryListContainer>
        </Box>
    );
};
